import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../lib/auth";

export default function Reviews() {
  const [d, setD] = useState({ average: 0, count: 0, reviews: [] });
  const [canWrite, setCanWrite] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/reviews").then(r => setD(r.data));
  }, []);

  // A logged-in user can post a review only if they have at least one
  // DELIVERED order that hasn't already been reviewed. This exactly mirrors
  // the backend rule enforced in POST /api/reviews.
  useEffect(() => {
    if (!user) { setCanWrite(false); return; }
    api.get("/my/orders")
      .then(r => setCanWrite(r.data.some(o => o.status === "DELIVERED" && !o.already_reviewed)))
      .catch(() => setCanWrite(false));
  }, [user]);

  return (
    <div>
      <Header/>
      <div className="max-w-5xl mx-auto pt-28 pb-16 px-4">
        <div className="mono-caption">Vault Reviews</div>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-2 mb-2">
          <h1 className="font-serif text-4xl font-bold gold-text">
            {d.average.toFixed(1)} <span className="text-[var(--av-text)] text-lg font-sans">/ 5.0</span>
          </h1>
          {canWrite ? (
            <Link
              to="/my-vault/reviews"
              data-testid="reviews-write-cta"
              className="btn-gold text-sm inline-flex items-center gap-2"
            >
              ★ Write a Review
            </Link>
          ) : user ? (
            <div
              data-testid="reviews-write-locked"
              className="text-xs text-[var(--av-text-dim)] max-w-xs text-right leading-snug"
            >
              You'll be able to write a review once your first Apex order is delivered.
            </div>
          ) : (
            <Link
              to="/auth?mode=login"
              data-testid="reviews-write-signin"
              className="btn-outline-gold text-sm inline-flex items-center gap-2"
            >
              Sign in to review
            </Link>
          )}
        </div>
        <div className="text-[var(--av-text-dim)] mb-3">{d.count} verified purchases</div>
        <div className="mb-8 flex items-start gap-2 p-3 rounded-lg bg-[rgba(212,175,55,0.06)] border border-[var(--av-gold)]/30 max-w-2xl">
          <span className="text-[var(--av-gold)] text-lg leading-none mt-0.5">✓</span>
          <p className="text-xs text-[var(--av-text-dim)]">
            <span className="text-[var(--av-gold)] font-semibold">Verified purchase only.</span>{" "}
            Every review below is tied to a real, delivered Arthur's Vault order — we never accept anonymous or unverified reviews.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {d.reviews.map(r => (
            <div key={r.id} className="vault-card p-6">
              <span className="pill pill-gold">Verified Purchase</span>
              <div className="text-[var(--av-gold)] my-2">
                {"★".repeat(r.rating)}
                <span className="text-[var(--av-text-muted)]">{"★".repeat(5 - r.rating)}</span>
              </div>
              <div className="font-semibold mb-1">{r.title}</div>
              <p className="text-sm text-[var(--av-text-dim)]">{r.body}</p>
              <div className="mono-caption mt-3">— {r.customer_name} · {r.apex_amount?.toLocaleString()} Apex</div>
              {r.owner_reply && (
                <div
                  data-testid={`owner-reply-${r.id}`}
                  className="mt-4 pt-4 border-t border-[var(--av-gold)]/25 pl-3 border-l-2 border-l-[var(--av-gold)]/60"
                >
                  <div className="mono-caption gold-text mb-1">↳ Arthur's Vault replied</div>
                  <p className="text-sm text-[var(--av-text)] whitespace-pre-wrap">{r.owner_reply}</p>
                  {r.owner_reply_at && (
                    <div className="text-[10px] text-[var(--av-text-muted)] mt-1">
                      {new Date(r.owner_reply_at).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {d.reviews.length === 0 && (
            <div className="text-[var(--av-text-dim)]">No verified reviews yet.</div>
          )}
        </div>
      </div>
      <Footer/>
    </div>
  );
}
